import { ApolloClient, InMemoryCache, HttpLink, gql, ApolloLink } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { NativeWebSocketLink } from './NativeWebSocketLink';

// Local SHA-256 hashing function compatible with browser Web Crypto API and Node.js (Jest)
export const sha256 = async (input: string): Promise<string> => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    try {
      const textAsBuffer = new TextEncoder().encode(input);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((item) => item.toString(16).padStart(2, '0')).join('');
    } catch {
      return '';
    }
  }
  
  // Fallback for Jest/Node environment
  try {
    const moduleName = 'crypto';
    const nodeCrypto = await import(/* @vite-ignore */ moduleName);
    return nodeCrypto.createHash('sha256').update(input).digest('hex');
  } catch {
    return '';
  }
};

// Configure the GraphQL endpoint pointing to the federation gateway
const graphqlUri = process.env.VITE_GRAPHQL_ENDPOINT || 'https://bfme.co.in/federation';

// Expose the endpoint globally so other micro-frontends (like mfe-expense-manager) can consume it
(window as any).WEALTHIQ_GRAPHQL_ENDPOINT = graphqlUri;

if (process.env.VITE_DEBUG === 'true' || typeof window !== 'undefined') {
  console.log('[wealthIQ] Active GraphQL Endpoint:', graphqlUri);
}

const httpLink = new HttpLink({
  uri: graphqlUri,
});

// Configure Persisted Queries Link using local sha256
const persistedQueriesLink = createPersistedQueryLink({
  sha256,
  // Some federation gateways return an HTML page for GET requests; prefer POST to ensure JSON responses
  useGETForHashedQueries: false,
});

// Create authentication context link
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('auth_token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      accept: 'application/json',
    },
  };
});

// Configure error handler link to auto-logout on 401 / UNAUTHENTICATED
const errorLink = onError(({ graphQLErrors, networkError }: any) => {
  let isUnauth = false;
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const msg = (err?.message || '').toLowerCase();
      const code = (err?.extensions?.code || '').toUpperCase();
      const category = (err?.extensions?.category || '').toLowerCase();
      if (
        msg.includes('unauthenticated') ||
        code === 'UNAUTHENTICATED' ||
        category === 'authentication' ||
        category === 'unauthenticated'
      ) {
        isUnauth = true;
      }
    }
  }
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    isUnauth = true;
  }
  if (isUnauth) {
    window.dispatchEvent(new CustomEvent('app-unauthorized'));
  }
});

const wsUri = process.env.VITE_WS_ENDPOINT || 'wss://bfme.co.in/app/wealthiqkey';
const wsLink = new NativeWebSocketLink(wsUri, graphqlUri);

const mainHttpLink = errorLink.concat(authLink).concat(
  ApolloLink.split(
    (operation) => operation.getContext().skipPersistedQueries === true,
    httpLink,
    persistedQueriesLink.concat(httpLink)
  )
);

export const client = new ApolloClient({
  link: ApolloLink.split(
    (operation) => {
      const { definitions } = operation.query;
      return definitions.some(
        (def) => def.kind === 'OperationDefinition' && def.operation === 'subscription'
      );
    },
    wsLink,
    mainHttpLink
  ),
  cache: new InMemoryCache(),
});

// Expose sample queries that will be picked up by the build-time extractor
/*
export const GET_USER_PORTFOLIO = gql`
  query GetUserPortfolio($userId: ID!) {
    user(id: $userId) {
      id
      name
      portfolio {
        id
        totalValue
        assets {
          symbol
          quantity
          price
        }
      }
    }
  }
`;

export const GET_MARKET_TRENDS = gql`
  query GetMarketTrends {
    marketTrends {
      symbol
      change
      price
    }
  }
`;
*/

export const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      access_token
      expires_in
      token_type
    }
  }
`;

export const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      status
      message
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      name
      first_name
      last_name
      email
      phone
      gender
      dob
      is_verified
      culture
      currency
      roles {
        id
        name
        description
      }
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers($name: String, $page: Int) {
    users(name: $name, page: $page) {
      data {
        id
        name
        first_name
        last_name
        email
        phone
        gender
        dob
        is_verified
        culture
        currency
        roles {
          id
          name
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        total
      }
    }
  }
`;

export const GET_ROLES = gql`
  query GetRoles {
    roles {
      id
      name
      description
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($name: String!, $email: String!, $password: String!, $first_name: String, $last_name: String, $phone: String, $gender: String, $dob: Date, $is_verified: Boolean, $culture: String, $currency: String) {
    createUser(name: $name, email: $email, password: $password, first_name: $first_name, last_name: $last_name, phone: $phone, gender: $gender, dob: $dob, is_verified: $is_verified, culture: $culture, currency: $currency) {
      id
      name
      email
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $name: String, $email: String, $password: String, $first_name: String, $last_name: String, $phone: String, $gender: String, $dob: Date, $is_verified: Boolean, $culture: String, $currency: String) {
    updateUser(id: $id, name: $name, email: $email, password: $password, first_name: $first_name, last_name: $last_name, phone: $phone, gender: $gender, dob: $dob, is_verified: $is_verified, culture: $culture, currency: $currency) {
      id
      name
      email
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      id
    }
  }
`;

export const ASSIGN_ROLE = gql`
  mutation AssignRole($user_id: ID!, $role_name: String!) {
    assignRole(user_id: $user_id, role_name: $role_name) {
      id
      roles {
        id
        name
      }
    }
  }
`;

export const REMOVE_ROLE = gql`
  mutation RemoveRole($user_id: ID!, $role_name: String!) {
    removeRole(user_id: $user_id, role_name: $role_name) {
      id
      roles {
        id
        name
      }
    }
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email) {
      status
      message
    }
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($token: String!, $email: String!, $password: String!, $password_confirmation: String!) {
    resetPassword(token: $token, email: $email, password: $password, password_confirmation: $password_confirmation) {
      status
      message
    }
  }
`;

export const VALIDATE_RESET_TOKEN_QUERY = gql`
  query ValidateResetToken($email: String!, $token: String!) {
    validateResetToken(email: $email, token: $token) {
      status
      message
    }
  }
`;

export const GET_NOTIFICATIONS_QUERY = gql`
  query GetNotifications($first: Int, $page: Int) {
    notifications(first: $first, page: $page) {
      data {
        id
        message
        type
        read
        created_at
      }
      paginatorInfo {
        currentPage
        lastPage
        total
        hasMorePages
      }
    }
  }
`;

export const MARK_NOTIFICATION_AS_READ_MUTATION = gql`
  mutation MarkNotificationAsRead($id: ID!) {
    markNotificationAsRead(id: $id) {
      id
      read
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_AS_READ_MUTATION = gql`
  mutation MarkAllNotificationsAsRead {
    markAllNotificationsAsRead
  }
`;
